import { useCallback, useContext, useEffect, useRef } from "react";

import ActionCableContext from "../contexts/actionCableContext";

export function useChannel({
  key,
  name,
  channel: identifier,
  onConnected,
  onReceived,
  onRejected,
  onDisconnected,
}: any) {
  const channel = useRef<any>();

  const { cable, channels, handlers } = useContext(ActionCableContext);

  // Keep latest callbacks in a ref so subscription closures never go stale.
  const onConnectedRef = useRef(onConnected);
  const onDisconnectedRef = useRef(onDisconnected);
  const onRejectedRef = useRef(onRejected);
  useEffect(() => {
    onConnectedRef.current = onConnected;
  }, [onConnected]);
  useEffect(() => {
    onDisconnectedRef.current = onDisconnected;
  }, [onDisconnected]);
  useEffect(() => {
    onRejectedRef.current = onRejected;
  }, [onRejected]);

  const onReceivedChannel = useCallback(
    (message: any) => {
      const channelHandlers = handlers.current[name];
      if (!channelHandlers) return;
      Object.values(channelHandlers.onReceived).forEach((handler: any) => {
        handler?.(message);
      });
    },
    [handlers, name]
  );

  const onConnectedChannel = useCallback(() => {
    onConnectedRef.current?.(channel.current);
  }, []);

  const onRejectedChannel = useCallback(() => {
    onRejectedRef.current?.();
  }, []);

  const onDisconnectedChannel = useCallback(() => {
    delete channels.current[name];
    channel.current = null;
    onDisconnectedRef.current?.(undefined);
  }, [channels, name]);

  useEffect(() => {
    if (!cable) return;

    // Ensure handler registry exists for this channel name.
    if (!handlers.current[name]) {
      handlers.current[name] = { onReceived: {} };
    }

    // Register this component's onReceived handler.
    handlers.current[name].onReceived[key || name] = onReceived;

    // Reuse an existing subscription if one already exists on the current cable.
    const existingChannel = channels.current[name];
    if (existingChannel) {
      channel.current = existingChannel;
      // Let caller know the channel is already connected.
      onConnectedRef.current?.(channel.current);
    } else {
      // Create a fresh subscription.
      channel.current = cable.subscriptions.create(identifier, {
        received: onReceivedChannel,
        connected: onConnectedChannel,
        disconnected: onDisconnectedChannel,
        rejected: onRejectedChannel,
      });
      channels.current[name] = channel.current;
    }

    const channelsMap = channels.current;
    const handlersMap = handlers.current;

    return () => {
      // Remove this component's handler.
      if (handlersMap[name]) {
        delete handlersMap[name].onReceived[key || name];
      }

      // Only unsubscribe the underlying cable subscription when the last
      // handler for this channel name is removed.
      const remainingHandlers = handlersMap[name]
        ? Object.keys(handlersMap[name].onReceived)
        : [];

      if (remainingHandlers.length === 0) {
        try {
          channel.current?.unsubscribe?.();
        } catch (_) {}
        delete channelsMap[name];
        delete handlersMap[name];
        channel.current = null;
      }
    };
  }, [
    cable,
    channels,
    handlers,
    identifier,
    key,
    name,
    onConnectedChannel,
    onDisconnectedChannel,
    onReceived,
    onReceivedChannel,
    onRejectedChannel,
  ]);

  return null;
}

export const Channel = useChannel;
